import { ApproveReturnConfirmDialog } from '@components/ApproveReturnConfirmDialog/ApproveReturnConfirmDialog';
import {
  ArrowSelect,
  DatePickerIcon,
  LoadingCommon,
  SessionYearMonthSelect,
} from '@components';
import EmptyDataTable from '@components/EmptyDataTable/EmptyDataTable';
import { TEN } from '@constants/common';
import { ArrowLeftIcon } from '@constants/svgs';
import { AppContext } from '@layouts/LayoutAdmin/LayoutAdmin';
import {
  Button,
  ConfigProvider,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import { useContext } from 'react';
import {
  ConfirmModalWrapper,
  FixedAssetLedgerWrapper,
} from './FixedAssetLedgerStyled';
import { ALL_DEVICE_CONSTANT, HEIGHT_TABLE } from './constant';
import useAllDevice from './hooks/useAllDevice';
import useApprovalPanel from './hooks/useApprovalPanel';
import SuffixIcon from './components/SuffixIcon';

const {
  NAME,
  EXTRA_FIELD_NAME,
  FILTER_LABEL,
  OPTIONS,
  PLACEHOLDER,
  MESSAGE,
  TITLE,
} = ALL_DEVICE_CONSTANT;

const FORM_ID = 'fixed-asset-ledger-form';

const FIELDS_CONFIG = {
  COMPANY: {
    required: false,
    name: NAME.COMPANY,
    label: FILTER_LABEL.COMPANY,
    className: 'fixed-asset-ledger_form-company',
  },
  STORE: {
    required: false,
    name: NAME.STORE,
    label: FILTER_LABEL.STORE,
    className: 'fixed-asset-ledger_form-store',
  },
  CATEGORY: {
    required: false,
    name: NAME.CATEGORY,
    label: FILTER_LABEL.CATEGORY,
    className: 'fixed-asset-ledger_form-category',
  },
  DEPARTMENT: {
    required: false,
    name: NAME.DEPARTMENT,
    label: FILTER_LABEL.DEPARTMENT,
    className: 'fixed-asset-ledger_form-department',
  },
  STATUS: {
    required: false,
    name: EXTRA_FIELD_NAME.STATUS,
    label: FILTER_LABEL.STATUS,
    className: 'fixed-asset-ledger_form-status',
  },
  OWNERSHIP: {
    required: false,
    name: NAME.OWNERSHIP,
    label: FILTER_LABEL.OWNERSHIP,
    className: 'fixed-asset-ledger_form-ownership',
  },
  PHOTO_AVAILABLE: {
    required: false,
    name: EXTRA_FIELD_NAME.PHOTO_AVAILABLE,
    label: FILTER_LABEL.PHOTO_AVAILABLE,
    className: 'fixed-asset-ledger_form-photo',
  },
  ASSET_NO: {
    required: false,
    name: EXTRA_FIELD_NAME.ASSET_NO,
    label: FILTER_LABEL.ASSET_MANAGEMENT_NO,
    className: 'fixed-asset-ledger_form-asset-no',
  },
  ACQ_DATE_FROM: {
    required: false,
    name: EXTRA_FIELD_NAME.ACQ_DATE_FROM,
  },
  ACQ_DATE_TO: {
    required: false,
    name: EXTRA_FIELD_NAME.ACQ_DATE_TO,
  },
  ACQ_PRICE_FROM: {
    required: false,
    name: EXTRA_FIELD_NAME.ACQ_PRICE_FROM,
  },
  ACQ_PRICE_TO: {
    required: false,
    name: EXTRA_FIELD_NAME.ACQ_PRICE_TO,
  },
};

function FixedAssetLedger() {
  const { handler, state } = useAllDevice();

  const dateFrom = Form.useWatch(EXTRA_FIELD_NAME.ACQ_DATE_FROM, state.form);
  const dateTo = Form.useWatch(EXTRA_FIELD_NAME.ACQ_DATE_TO, state.form);
  const priceFrom = Form.useWatch(EXTRA_FIELD_NAME.ACQ_PRICE_FROM, state.form);
  const priceTo = Form.useWatch(EXTRA_FIELD_NAME.ACQ_PRICE_TO, state.form);

  // 承認/差し戻し (VAMS_PC_009 [4.12] moved here, Option 1 — before エクスポート) react to the
  // current 棚卸年月/店舗 selection directly, independent of whether 検索 has been pressed.
  const { role } = useContext(AppContext);
  const watchedFiscalMonth = Form.useWatch(
    EXTRA_FIELD_NAME.FISCAL_MONTH,
    state.form
  );
  const watchedStoreCode = Form.useWatch(NAME.STORE, state.form) || '';
  const approvalSessionCode = watchedFiscalMonth
    ? String(watchedFiscalMonth).replace('/', '')
    : '';
  const selectedStoreOption = state.storesSelect.find(
    (s) => s.value === watchedStoreCode
  );
  const approvalStoreLabel = selectedStoreOption?.label ?? '';
  // storesSelect.value is storeCode; the store's Guid is carried in .children (see useAllDeviceApi).
  const approvalStoreId =
    watchedStoreCode === '' ? '' : selectedStoreOption?.children ?? '';
  const approval = useApprovalPanel({
    role,
    sessionCode: approvalSessionCode,
    storeCode: watchedStoreCode,
    storeId: approvalStoreId,
    storeLabel: approvalStoreLabel,
    totalStoreCount: state.storesSelect.length,
  });

  const isDateRangeInvalid =
    !!dateFrom && !!dateTo && dayjs(dateTo).isBefore(dayjs(dateFrom), 'day');

  const isPriceRangeInvalid = (() => {
    if (!priceFrom || !priceTo) {
      return false;
    }
    const from = Number(String(priceFrom).replace(/,/g, ''));
    const to = Number(String(priceTo).replace(/,/g, ''));
    return to < from;
  })();

  const rangeWarnings = [
    isDateRangeInvalid ? MESSAGE.DATE_RANGE_INVALID : undefined,
    isPriceRangeInvalid ? MESSAGE.COST_RANGE_INVALID : undefined,
  ].filter(Boolean);

  const handleFinish = (values: Parameters<typeof handler.handleSearch>[0]) => {
    if (rangeWarnings.length) {
      return;
    }
    handler.handleSearch(values);
  };

  return (
    <LoadingCommon loading={state.loadingPage}>
      <FixedAssetLedgerWrapper listdata={state.resultData.data.length}>
        <div className="fixed-asset-ledger_header">
          <div className="fixed-asset-ledger_header-title">
            <button onClick={handler.handleNavigateBack}>
              <ArrowLeftIcon />
            </button>
            <h1>{TITLE.FIXED_ASSET_LEDGER}</h1>
          </div>

          <div className="fixed-asset-ledger_header-btn">
            {approval.canApprove && (
              <span className="fixed-asset-ledger_approval-buttons">
                <Tooltip title={approval.approveDisabledReason || undefined}>
                  <Button
                    type="primary"
                    className="fixed-asset-ledger_approve-btn"
                    disabled={approval.approveDisabled}
                    onClick={approval.handleApprove}
                  >
                    {TITLE.APPROVE_BTN}
                  </Button>
                </Tooltip>
                <Button
                  danger
                  type="primary"
                  className="fixed-asset-ledger_return-btn"
                  disabled={approval.returnDisabled}
                  onClick={approval.handleReturn}
                >
                  {TITLE.RETURN_BTN}
                </Button>
              </span>
            )}
            <Button
              loading={state.loadingExport}
              onClick={handler.handleExportData}
            >
              {TITLE.BTN_EXPORT}
            </Button>
            <Button onClick={handler.handleNavigateToInventoryReport}>
              {TITLE.OUTPUT_INVENTORY_REPORT}
            </Button>
          </div>
        </div>

        <div className="fixed-asset-ledger_form fixed-asset-ledger_ledger-form">
          <Form
            id={FORM_ID}
            layout="vertical"
            form={state.form}
            onFinish={handleFinish}
            initialValues={{
              [NAME.COMPANY]: '',
              [NAME.STORE]: '',
              [NAME.CATEGORY]: '',
              [NAME.DEPARTMENT]: '',
              [NAME.OWNERSHIP]: '',
              [EXTRA_FIELD_NAME.STATUS]: '',
              [EXTRA_FIELD_NAME.PHOTO_AVAILABLE]: '',
            }}
          >
            <div className="fixed-asset-ledger_form-month">
              <Form.Item
                required
                name={EXTRA_FIELD_NAME.FISCAL_MONTH}
                label={FILTER_LABEL.FISCAL_MONTH}
                className="fixed-asset-ledger_form-fiscal-month"
              >
                <SessionYearMonthSelect sessions={state.sessionsSelect} />
              </Form.Item>
            </div>

            <div className="fixed-asset-ledger_form-filters-row">
              <Form.Item {...FIELDS_CONFIG.COMPANY}>
                <Select
                  size="large"
                  options={state.companiesSelect}
                  disabled={state.loadingOther}
                  suffixIcon={<SuffixIcon loading={state.loadingOther} />}
                />
              </Form.Item>

              <Form.Item {...FIELDS_CONFIG.STORE}>
                <Select
                  size="large"
                  options={state.storesSelect}
                  disabled={state.isStoreRole || state.loadingOther}
                  suffixIcon={<SuffixIcon loading={state.loadingOther} />}
                />
              </Form.Item>

              <Form.Item {...FIELDS_CONFIG.CATEGORY}>
                <Select
                  size="large"
                  options={state.assetTypesSelect}
                  disabled={state.loadingOther}
                  suffixIcon={<SuffixIcon loading={state.loadingOther} />}
                />
              </Form.Item>

              <Form.Item {...FIELDS_CONFIG.DEPARTMENT}>
                <Select
                  size="large"
                  options={state.departmentsSelect}
                  disabled={state.loadingOther}
                  suffixIcon={<SuffixIcon loading={state.loadingOther} />}
                />
              </Form.Item>

              <Form.Item {...FIELDS_CONFIG.STATUS}>
                <Select
                  size="large"
                  options={OPTIONS.STATUS}
                  suffixIcon={<ArrowSelect />}
                />
              </Form.Item>

              <Form.Item {...FIELDS_CONFIG.OWNERSHIP}>
                <Select
                  size="large"
                  options={state.locationsSelect}
                  disabled={state.loadingOther}
                  suffixIcon={<SuffixIcon loading={state.loadingOther} />}
                />
              </Form.Item>

              <Form.Item {...FIELDS_CONFIG.PHOTO_AVAILABLE}>
                <Select
                  size="large"
                  options={OPTIONS.PHOTO_AVAILABLE}
                  suffixIcon={<ArrowSelect />}
                />
              </Form.Item>

              <Form.Item {...FIELDS_CONFIG.ASSET_NO}>
                <Input
                  size="large"
                  maxLength={20}
                  placeholder={PLACEHOLDER.INPUT}
                />
              </Form.Item>

              <Form.Item
                label={FILTER_LABEL.ACQUISITION_MONTH}
                className="fixed-asset-ledger_form-date-stack"
              >
                <div className="fixed-asset-ledger_stack-field">
                  <Form.Item {...FIELDS_CONFIG.ACQ_DATE_FROM} noStyle>
                    <DatePicker
                      size="large"
                      format={['YYYY/MM/DD', 'YYYY-MM-DD', 'YYYYMMDD']}
                      suffixIcon={<DatePickerIcon disable={false} />}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          const input = e.target as HTMLInputElement;
                          if (/[a-zA-Z]/.test(input?.value ?? '')) {
                            // Force-clear the native input synchronously so antd's
                            // internal text buffer is reset before React re-renders.
                            const nativeSetter =
                              Object.getOwnPropertyDescriptor(
                                window.HTMLInputElement.prototype,
                                'value'
                              )?.set;
                            nativeSetter?.call(input, '');
                            input.dispatchEvent(
                              new Event('input', { bubbles: true })
                            );
                            state.form.setFieldValue(
                              EXTRA_FIELD_NAME.ACQ_DATE_FROM,
                              undefined
                            );
                          }
                        }
                      }}
                    />
                  </Form.Item>
                  <Form.Item {...FIELDS_CONFIG.ACQ_DATE_TO} noStyle>
                    <DatePicker
                      size="large"
                      status={isDateRangeInvalid ? 'error' : undefined}
                      format={['YYYY/MM/DD', 'YYYY-MM-DD', 'YYYYMMDD']}
                      suffixIcon={<DatePickerIcon disable={false} />}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          const input = e.target as HTMLInputElement;
                          if (/[a-zA-Z]/.test(input?.value ?? '')) {
                            // Force-clear the native input synchronously so antd's
                            // internal text buffer is reset before React re-renders.
                            const nativeSetter =
                              Object.getOwnPropertyDescriptor(
                                window.HTMLInputElement.prototype,
                                'value'
                              )?.set;
                            nativeSetter?.call(input, '');
                            input.dispatchEvent(
                              new Event('input', { bubbles: true })
                            );
                            state.form.setFieldValue(
                              EXTRA_FIELD_NAME.ACQ_DATE_TO,
                              undefined
                            );
                          }
                        }
                      }}
                    />
                  </Form.Item>
                </div>
              </Form.Item>

              <Form.Item
                label={FILTER_LABEL.ACQUISITION_PRICE}
                className="fixed-asset-ledger_form-price-stack"
              >
                <div className="fixed-asset-ledger_stack-field">
                  <Form.Item
                    {...FIELDS_CONFIG.ACQ_PRICE_FROM}
                    noStyle
                    getValueFromEvent={(e) =>
                      e.target.value.replace(/[^0-9,]/g, '')
                    }
                  >
                    <Input
                      size="large"
                      placeholder={PLACEHOLDER.INPUT}
                      inputMode="numeric"
                    />
                  </Form.Item>
                  <Form.Item
                    {...FIELDS_CONFIG.ACQ_PRICE_TO}
                    noStyle
                    getValueFromEvent={(e) =>
                      e.target.value.replace(/[^0-9,]/g, '')
                    }
                  >
                    <Input
                      size="large"
                      status={isPriceRangeInvalid ? 'error' : undefined}
                      placeholder={PLACEHOLDER.INPUT}
                      inputMode="numeric"
                    />
                  </Form.Item>
                </div>
              </Form.Item>

              <Button
                className="fixed-asset-ledger_search-btn"
                htmlType="submit"
                form={FORM_ID}
                autoInsertSpace={false}
              >
                {TITLE.BTN_SEARCH}
              </Button>
            </div>

            <div className="fixed-asset-ledger_form-footer">
              {(state.searchWarning || rangeWarnings.length > 0) && (
                <div className="fixed-asset-ledger_search-warning">
                  {state.searchWarning
                    ? state.searchWarning
                    : rangeWarnings.map((message) => (
                        <div key={message}>{message}</div>
                      ))}
                </div>
              )}
              <div className="fixed-asset-ledger_form-object">
                {state.resultData.totalCount} /{' '}
                {state.resultData.companyTotalCount} {FILTER_LABEL.PAGE_INFO}
              </div>
            </div>
          </Form>
        </div>

        <div className="fixed-asset-ledger_table">
          <ConfigProvider
            theme={{
              components: {
                Table: {
                  headerBg: 'var(--background-header-table)',
                  borderColor: 'var(--color-border-primary)',
                  headerColor: 'var(--color-header-table)',
                  fontWeightStrong: 700,
                },
              },
            }}
            renderEmpty={() =>
              !state.resultData.data.length ? (
                <EmptyDataTable message={MESSAGE.NO_DATA} />
              ) : undefined
            }
          >
            <Table
              dataSource={state.resultData.data}
              columns={state.columns}
              bordered
              showSorterTooltip={false}
              scroll={{
                x: 2120,
                y:
                  state.resultData.data.length > TEN ? HEIGHT_TABLE : undefined,
              }}
              pagination={false}
              onScroll={handler.handleScroll}
              onChange={handler.handleChangeTable}
              virtual={state.resultData.data.length > 0}
            />
          </ConfigProvider>
        </div>
      </FixedAssetLedgerWrapper>

      <Modal
        open={!!state.errorDialog}
        onOk={handler.handleCloseErrorDialog}
        onCancel={handler.handleCloseErrorDialog}
        cancelButtonProps={{ style: { display: 'none' } }}
        centered
        closeIcon={false}
      >
        <p>{state.errorDialog}</p>
      </Modal>

      <ConfirmModalWrapper
        centered
        closeIcon={false}
        open={!!state.deleteTarget}
        title={TITLE.DELETE_CONFIRM_TITLE}
        confirmLoading={state.loadingPage}
        onOk={handler.handleConfirmDelete}
        onCancel={handler.handleCancelDelete}
        maskClosable={false}
        cancelText={TITLE.BTN_CANCEL}
        okText={TITLE.BTN_OK}
        width={412}
      >
        <p className="fixed-asset-ledger_modal-download">
          {MESSAGE.DELETE_CONFIRM}
        </p>
      </ConfirmModalWrapper>

      <ApproveReturnConfirmDialog
        open={approval.dialogOpen}
        loading={approval.dialogLoading}
        type={approval.dialogType}
        labels={{
          approveConfirmTitle: TITLE.APPROVE_CONFIRM_TITLE,
          returnConfirmTitle: TITLE.RETURN_CONFIRM_TITLE,
          approveConfirm: MESSAGE.APPROVE_CONFIRM,
          returnConfirm: MESSAGE.RETURN_CONFIRM,
          approveBtn: TITLE.APPROVE_BTN,
          returnBtn: TITLE.RETURN_BTN,
          cancelBtn: TITLE.BTN_CANCEL,
          columnLabel: TITLE.DIALOG_COLUMN_LABEL,
          columnValue: TITLE.DIALOG_COLUMN_VALUE,
          rowStoreLabel: TITLE.DIALOG_ROW_STORE,
          rowSessionLabel: TITLE.DIALOG_ROW_SESSION,
        }}
        sessionDisplay={String(watchedFiscalMonth ?? '')}
        storeSummary={approval.storeSummary}
        bulkSummary={approval.bulkSummary}
        onCancel={approval.handleCancelDialog}
        onOk={approval.handleConfirmDialog}
      />
    </LoadingCommon>
  );
}

export { FixedAssetLedger };
